import { useField, useFormikContext } from 'formik';
import { parseIntNumberWithPoints } from 'gtx-react/utils/parseIntWithPoints';
import { formatNumberToLocale } from 'lib/formatter';

const parseCurrencyFormattedNum = (
    value: string,
    hasFractionDigits?: boolean
) => {
    if (hasFractionDigits) {
        return typeof value === 'string' && value.indexOf('.') > -1
            ? parseFloat(value.replace(/\./g, '').replace(/\,/g, '.'))
            : parseFloat(value.replace(/\,/g, '.'));
    } else {
        return parseIntNumberWithPoints(value);
    }
};

const fillWithZero = (times: number) => {
    let str = '';
    for (let i = 0; i < times; i++) {
        str += '0';
    }
    return str;
};

/**
 * Handling inputs for numbers formatted in string with thousand separator (the dot `.`) and fraction-digits separator (the comma `,`) (example: `10.666,50`); it also prevents user to type non-allowed characters.
 *
 * Replaces `onChange` prop with custom parsing/formatting string logics.
 *
 * In 'fraction-digits' mode (when second parameter is defined and > 0), it also replaces `onBlur` prop to optionally normalize the incomplete input of the user
 * (example (with maximumFractionDigits = 2): '100,' and '100,0' become '100,00')
 *
 * @param {string} fieldName - required to identify the field
 * @param {number} maximumFractionDigits Optional, defaults for integer only
 * @returns {Object} {fieldProps, fieldMeta}
 */
export const useFormikFormattedNumField = (
    fieldName: string,
    maximumFractionDigits?: Intl.NumberFormatOptions['maximumFractionDigits']
) => {
    const { setFieldValue, values } = useFormikContext();
    const [fieldProps, fieldMeta] = useField(fieldName);

    const hasFractionDigits =
        maximumFractionDigits && maximumFractionDigits > 0 ? true : false;

    const onChange = (e: React.ChangeEvent<any>) => {
        const value = e.target.value;

        const currentFractionDigits =
            value.indexOf(',') > -1
                ? value?.length - (value.indexOf(',') + 1)
                : 0;

        const hasTrailingComma =
            value.indexOf(',') > 0 && value.indexOf(',') + 1 === value?.length;

        if (hasFractionDigits && maximumFractionDigits && currentFractionDigits > maximumFractionDigits)
            return;

        const options: Pick<
            Intl.NumberFormatOptions,
            'maximumFractionDigits' | 'minimumFractionDigits'
        > = {
            minimumFractionDigits: hasFractionDigits
                ? currentFractionDigits
                : undefined,
            maximumFractionDigits: hasFractionDigits
                ? maximumFractionDigits
                : undefined,
        };

        setFieldValue(
            fieldName,
            !isNaN(parseCurrencyFormattedNum(value, hasFractionDigits))
                ? `${formatNumberToLocale(
                      parseCurrencyFormattedNum(value, hasFractionDigits),
                      options
                  )}${hasFractionDigits && hasTrailingComma ? ',' : ''}`
                : ''
        );
    };

    const onBlur = !hasFractionDigits
        ? fieldProps.onBlur
        : (e: any) => {
              fieldProps.onBlur(e);

              const currentValue: string = values![fieldName];

              if (currentValue === '0') {
                  setFieldValue(fieldName, '');
              } else {
                  const indexofComma = currentValue.indexOf(',');

                  const currentFractionDigits =
                      indexofComma > -1
                          ? currentValue?.length - (indexofComma + 1)
                          : 0;

                  // filling number-string with missing comma and/or after-comma zeros
                  if (
                      currentValue?.length &&
                      maximumFractionDigits &&
                      currentFractionDigits < maximumFractionDigits
                  ) {
                      setFieldValue(
                          fieldName,
                          `${currentValue}${
                              indexofComma < 0 ? ',' : ''
                          }${fillWithZero(
                              maximumFractionDigits - currentFractionDigits
                          )}`
                      );
                  }
              }
          };

    return {
        fieldProps: {
            ...fieldProps,
            onChange,
            onBlur,
        },
        fieldMeta,
    };
};
